import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success('Message sent! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setLoading(false);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Layout>
      <div style={{ background: 'var(--color-background)' }}>
        {/* Hero Section */}
        <section className="section-padding" style={{ background: 'var(--color-primary)' }}>
          <div className="container-custom text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-text-on-accent)' }}>
              Get in Touch
            </h1>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--color-text-on-accent)', opacity: 0.9 }}>
              Have questions? Need support? Want to discuss bulk orders? We're here to help.
            </p>
          </div>
        </section>

        {/* Contact Info & Form */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <Card className="luxury-card">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-4">
                    <div
                      className="h-16 w-16 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--color-accent)', color: 'var(--color-text-on-accent)' }}
                    >
                      <Phone size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Phone</h3>
                  <a href="tel:+254703207300" style={{ color: 'var(--color-accent)' }} className="hover:underline">
                    +254 703 207 300
                  </a>
                </CardContent>
              </Card>

              <Card className="luxury-card">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-4">
                    <div
                      className="h-16 w-16 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--color-accent)', color: 'var(--color-text-on-accent)' }}
                    >
                      <Mail size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Email</h3>
                  <a href="mailto:aethernexus05@gmail.com" style={{ color: 'var(--color-accent)' }} className="hover:underline">
                    aethernexus05@gmail.com
                  </a>
                </CardContent>
              </Card>

              <Card className="luxury-card">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-4">
                    <div
                      className="h-16 w-16 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--color-accent)', color: 'var(--color-text-on-accent)' }}
                    >
                      <MapPin size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Visit Us</h3>
                  <a href="/store-locator" style={{ color: 'var(--color-accent)' }} className="hover:underline">
                    View All Locations
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="max-w-3xl mx-auto">
              <Card className="luxury-card">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold mb-6 text-center" style={{ color: 'var(--color-text-primary)' }}>
                    Send Us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="luxury-input"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="luxury-input"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="luxury-input"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="luxury-input"
                          data-testid="contact-subject"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="luxury-input w-full"
                        data-testid="contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="luxury-button-primary w-full"
                      data-testid="contact-submit"
                    >
                      {loading ? 'Sending...' : (
                        <>
                          <Send size={20} className="mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Business Hours */}
        <section className="section-padding" style={{ background: 'var(--color-background-secondary)' }}>
          <div className="container-custom">
            <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: 'var(--color-text-primary)' }}>
              Business Hours
            </h2>
            <div className="max-w-2xl mx-auto">
              <Card className="luxury-card">
                <CardContent className="p-8">
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-primary)' }}>Monday - Saturday</span>
                      <span style={{ color: 'var(--color-accent)' }} className="font-semibold">8:00 AM - 7:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-primary)' }}>Sunday</span>
                      <span style={{ color: 'var(--color-accent)' }} className="font-semibold">10:00 AM - 5:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--color-text-primary)' }}>Response Time</span>
                      <span style={{ color: 'var(--color-accent)' }} className="font-semibold">Within 24 hours</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ContactPage;